/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import Pagination from '@mui/material/Pagination';

interface IRecordLensesProps {
  row: any;
  handleSelectLens: any;
};

interface ScreeningResult {
  biteLens?: string;
  results?: any;

}

type ColumnKeys = 'biteLens' | 'results';

const RecordLenses: React.FC<IRecordLensesProps> = ({ row, handleSelectLens }) => {
  const totalResults = row?.body?.hits?.total?.value;

  const [ hitsData, setHitsData ] = useState<any>(row?.body?.aggregations?.lensAgg?.buckets);
  const [ sortConfig, setSortConfig ] = useState<{ key: keyof ScreeningResult; direction: 'asc' | 'desc' }>({
    key: 'biteLens',
    direction: 'asc',
  });

  const [ svgIcons, setSvgIcons ] = useState<Record<ColumnKeys, JSX.Element>>({
    biteLens: <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
      <path
        d='M7.99967 12.6666V3.33331M7.99967 3.33331L3.33301 7.99998M7.99967 3.33331L12.6663 7.99998'
        stroke='#BDBEC1'
        strokeWidth='1.33333'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>,
    results: <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
      <path
        d='M7.99967 12.6666V3.33331M7.99967 3.33331L3.33301 7.99998M7.99967 3.33331L12.6663 7.99998'
        stroke='#BDBEC1'
        strokeWidth='1.33333'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>,
  });


  useEffect(() => {
    const newSvgIcons = { ...svgIcons };

    // Update the SVG icons based on the sorting state
    (Object.keys(newSvgIcons) as ColumnKeys[]).forEach((column) => { // Cast keys to ColumnKeys[]
      if (column === sortConfig.key) {
        newSvgIcons[column] = sortConfig.direction === 'asc' ? (
          <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
            <path
              d='M7.99967 12.6666V3.33331M7.99967 3.33331L3.33301 7.99998M7.99967 3.33331L12.6663 7.99998'
              stroke='#BDBEC1'
              strokeWidth='1.33333'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        ) : (
          <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
            <path
              d='M7.99967 3.33331V12.6666M7.99967 12.6666L3.33301 7.99998M7.99967 12.6666L12.6663 7.99998'
              stroke='#BDBEC1'
              strokeWidth='1.33333'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        );
      } else {
        // Reset the icon to the default state
        newSvgIcons[column] = (
          <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
            <path
              d='M7.99967 12.6666V3.33331M7.99967 3.33331L3.33301 7.99998M7.99967 3.33331L12.6663 7.99998'
              stroke='#BDBEC1'
              strokeWidth='1.33333'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        );
      }
    });

    setSvgIcons(newSvgIcons);
  }, [ sortConfig ]);

  // Sorting function
  const sortData = (key: string) => {
    if (!Array.isArray(hitsData)) {
      return;
    }
    // Type assertion to ensure the key is one of the allowed keys
    const typedKey = key as keyof ScreeningResult;
    const sortedData = [ ...hitsData ];

    if (key === 'biteLens'){
      if (sortConfig.key === typedKey && sortConfig.direction === 'asc') {
        sortedData.sort((a, b) => (a.key > b.key ? -1 : 1));
        setSortConfig({ key: typedKey, direction: 'desc' });
      } else {
        sortedData.sort((a, b) => (a.key > b.key ? 1 : -1));
        setSortConfig({ key: typedKey, direction: 'asc' });
      }
      setHitsData(sortedData);
    } else {
      if (sortConfig.key === typedKey && sortConfig.direction === 'asc') {
        sortedData.sort((a, b) => (a.doc_count > b.doc_count ? -1 : 1));
        setSortConfig({ key: typedKey, direction: 'desc' });
      } else {
        sortedData.sort((a, b) => (a.doc_count > b.doc_count ? 1 : -1));
        setSortConfig({ key: typedKey, direction: 'asc' });
      }
      setHitsData(sortedData);
    }
  };

  return (
    <div className='screening-results-container'>
      <>
        <div className='screening-results-columns'>
          <div className='screening-table-title-column' onClick={() => sortData('biteLens')}>
            <div className='screening-table-title-column-inner'>
              <div className='screening-table-column-text'>
            BITE Lens {svgIcons.biteLens}
              </div>
            </div>
          </div>
          <div className='screening-table-title-column' onClick={() => sortData('results')}>
            <div className='screening-table-title-column-inner'>
              <div className='screening-table-column-text'>
            Results {svgIcons.results}
              </div>
            </div>
          </div>
          <div className='screening-table-placeholder-column'></div>
        </div>
        <div className='screening-results-table-container'>
          {hitsData?.map((lensObj: any, index: number) => (
            <div className='screening-results-row' onClick={() => handleSelectLens(lensObj.key)} key={index} >
              <div className='screening-results-row-default-cell'>
                <div
                  className='screening-results-row-default-cell-inner'
                >
                  <div className='screening-results-row-link-cell-text'>
                    {lensObj?.key}
                  </div>
                </div>
              </div>
              <div className='screening-results-row-default-cell'>
                <div className='screening-results-row-default-cell-inner'>
                  <div className='screening-results-row-default-cell-text'>
                    {lensObj?.doc_count}
                  </div>
                </div>
              </div>
              <div className='screening-results-row-placeholder-cell'>
                <svg style={{ cursor: 'pointer' }} xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                  <path d='M15.75 10.5V12.15C15.75 13.4101 15.75 14.0402 15.5048 14.5215C15.289 14.9448 14.9448 15.289 14.5215 15.5048C14.0402 15.75 13.4101 15.75 12.15 15.75H10.5M7.5 2.25H5.85C4.58988 2.25 3.95982 2.25 3.47852 2.49524C3.05516 2.71095 2.71095 3.05516 2.49524 3.47852C2.25 3.95982 2.25 4.58988 2.25 5.85V7.5M11.25 6.75L15.75 2.25M15.75 2.25H11.25M15.75 2.25V6.75M6.75 11.25L2.25 15.75M2.25 15.75H6.75M2.25 15.75L2.25 11.25' stroke='#EFF1F6' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                </svg>
              </div>
            </div>
          ))}
        </div>
      </>
      {totalResults && (
        <div className='screening-results-footer'>
          <div className='screening-results-pagination-text'>
            Page {1} of {1}
          </div>
          <div className='screening-results-footer-action-group'>
            <div className='hb-pagination'>
              <Pagination
                size='large'
                count={1}
                shape='rounded'
                variant='text'
                page={1}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordLenses;
